import { Card, DatePicker, Flex, Form, Input } from 'antd'
import { FormInstance } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'

import { useTreatmentServiceGetCd4ContextEndpoint } from 'src/api/queries'
import { Icd10AmElementType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { Text } from 'src/components'
import { HivClassifierSelect } from 'src/features/hiv-anamnesis/components/fields/HivClassifierSelect'
import { useClassifierElements } from 'src/features/hiv-anamnesis/hooks/useClassifierElements'
import { useHivAnamnesisClassifiers } from 'src/features/hiv-anamnesis/hooks/useHivAnamnesisClassifiers'
import { aidsCategoryLetterOf } from 'src/features/hiv-anamnesis/utils/aidsCategory'
import { cd4CategoryOf, hasHivDiagnosis } from 'src/features/persons/utils'
import {
  dayMonthYearFormatWithDot,
  dayMonthYearHourMinutesFormatWithDot,
  disabledFutureDate
} from 'src/libs'
import { useLocales } from 'src/locales'

import { OpportunisticDiseaseInfiniteSelect } from './OpportunisticDiseaseInfiniteSelect'
import { useStyles } from './styles'

type Props = {
  form: FormInstance
}

const formatCd4Date = (date?: string | null) =>
  date ? dayjs(date).format(dayMonthYearFormatWithDot) : ''

/** The latest CD4 count and percent, and the CD4 category the count suggests for 55.2. */
const Cd4Context = () => {
  const { t } = useLocales()
  const palette = useTheme()
  const { personId } = useParams()

  const { data: context } = useTreatmentServiceGetCd4ContextEndpoint(
    { personId: String(personId) },
    [personId],
    { enabled: !!personId }
  )

  const hasCount =
    context?.latestCd4Count !== undefined && context?.latestCd4Count !== null
  const hasPercent =
    context?.latestCd4Percent !== undefined &&
    context?.latestCd4Percent !== null

  if (!hasCount && !hasPercent) {
    return (
      <Text color={palette.colorTextDescription}>
        {t('consultations.cd4_no_results')}
      </Text>
    )
  }

  const suggested = cd4CategoryOf(context?.latestCd4Count)

  return (
    <Flex vertical gap={4}>
      <Text color={palette.colorTextDescription}>
        {[
          hasCount &&
            t('consultations.cd4_count_latest', {
              count: context?.latestCd4Count ?? undefined,
              date: formatCd4Date(context?.latestCd4CountDate)
            }),
          hasPercent &&
            t('consultations.cd4_percent_latest', {
              percent: context?.latestCd4Percent ?? undefined,
              date: formatCd4Date(context?.latestCd4PercentDate)
            })
        ]
          .filter(Boolean)
          .join(' · ')}
      </Text>

      {suggested && (
        <Text color={palette.colorTextDescription}>
          {t('consultations.cd4_suggested_category', suggested)}
        </Text>
      )}
    </Flex>
  )
}

export const ConsultationFinalDiagnosticCard = ({ form }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { aidsClinicalCategoryId, aidsClinicalConditionsIdOf } =
    useHivAnamnesisClassifiers()
  const { elements: aidsCategories } = useClassifierElements(
    aidsClinicalCategoryId
  )

  const aidsCategoryLetterOfId = (id?: string) =>
    aidsCategoryLetterOf(aidsCategories.find((element) => element.id === id))

  const palette = useTheme()

  const notes = Form.useWatch(['assessment', 'comments'], form)

  return (
    <Card title={t('consultations.final_diagnostic')}>
      <Form.Item shouldUpdate>
        {({ getFieldValue }) => {
          const assessment = getFieldValue('assessment')

          const hasAssessment = Object.values(assessment ?? {}).find((val) =>
            Boolean(val)
          )

          // 55.2 is required once a diagnosis is an HIV infection (as the API checks).
          const isHiv = hasHivDiagnosis(assessment?.finalDiagnosticIds)

          // The clinical conditions offered are those of the selected category's letter (A, B or C).
          const categoryLetter = aidsCategoryLetterOfId(
            assessment?.aidsClinicalCategoryId
          )

          return (
            <Flex vertical gap={24}>
              <Form.Item
                name={['assessment', 'finalDiagnosticIds']}
                label={t('consultations.final_diagnostic')}
                rules={hasAssessment ? requiredRules : []}
              >
                <OpportunisticDiseaseInfiniteSelect
                  fieldName={['assessment', 'finalDiagnosticIds']}
                  form={form}
                  placeholder={t('common.select_from_list')}
                />
              </Form.Item>
              <Form.Item
                name={['assessment', 'opportunisticDiseaseIds']}
                label={t('treatments.opportunistic_diseases')}
                rules={hasAssessment ? requiredRules : []}
              >
                <OpportunisticDiseaseInfiniteSelect
                  fieldName={['assessment', 'opportunisticDiseaseIds']}
                  suggestedType={Icd10AmElementType.OPPORTUNISTIC_DISEASE}
                  form={form}
                  placeholder={t('common.select_from_list')}
                />
              </Form.Item>

              <Form.Item
                name={['assessment', 'dateOfDiseaseOnset']}
                label={t('epidemiology.date_time_of_disease_onset')}
              >
                <DatePicker
                  className="full-width"
                  showTime
                  showSecond={false}
                  needConfirm={false}
                  format={dayMonthYearHourMinutesFormatWithDot}
                  disabledDate={disabledFutureDate}
                />
              </Form.Item>

              <Flex vertical gap={8}>
                <Form.Item
                  name={['assessment', 'aidsClinicalCategoryId']}
                  // The consultation form hides required marks, so 55.2 shows its own
                  label={
                    <span>
                      {t('consultations.aids_clinical_category')}
                      {isHiv && <span className={styles.requiredMark}> *</span>}
                    </span>
                  }
                  extra={
                    isHiv
                      ? undefined
                      : t('consultations.aids_clinical_category_hint')
                  }
                  rules={
                    isHiv
                      ? [
                          {
                            required: true,
                            message: t(
                              'consultations.aids_clinical_category_required'
                            )
                          }
                        ]
                      : []
                  }
                >
                  <HivClassifierSelect
                    classifierId={aidsClinicalCategoryId}
                    // Only a user's change of letter drops the conditions; prefills keep them.
                    onChange={(value) => {
                      if (
                        aidsCategoryLetterOfId(
                          typeof value === 'string' ? value : undefined
                        ) !== categoryLetter
                      ) {
                        form.setFieldValue(
                          ['assessment', 'aidsClinicalConditionIds'],
                          undefined
                        )
                      }
                    }}
                  />
                </Form.Item>

                {categoryLetter && (
                  <Form.Item
                    name={['assessment', 'aidsClinicalConditionIds']}
                    label={t('consultations.aids_clinical_conditions', {
                      category: categoryLetter.toUpperCase()
                    })}
                    extra={t('consultations.aids_clinical_conditions_hint')}
                  >
                    <HivClassifierSelect
                      mode="multiple"
                      classifierId={aidsClinicalConditionsIdOf(categoryLetter)}
                    />
                  </Form.Item>
                )}

                <Cd4Context />
              </Flex>

              <Flex vertical>
                <Form.Item
                  name={['assessment', 'comments']}
                  label={t('form.notes')}
                  rules={[{ max: 1000 }]}
                >
                  <Input.TextArea
                    className={styles.textarea}
                    placeholder={t(
                      'consultations.preliminary_notes_placeholder'
                    )}
                  />
                </Form.Item>

                <Text
                  className={styles.textareaText}
                  color={palette.colorTextDescription}
                >
                  {notes?.length ?? 0} / 1000
                </Text>
              </Flex>
            </Flex>
          )
        }}
      </Form.Item>
    </Card>
  )
}
